import { ActionReducer, createReducer, on } from '@ngrx/store';

import { setLastPageStateTrailers } from '@store/trailers/lastStateTrailers.actions';
import {
  addTrailerGroup,
  deleteTrailerGroup,
  loadTrailerGroups,
  loadTrailerGroupsFailure,
  loadTrailerGroupsSuccess,
  updateTrailerGroup,
} from '@store/trailers/trailerGroups.actions';
import {
  addTrailer,
  deleteTrailer,
  loadTrailers,
  loadTrailersFailure,
  loadTrailersSuccess,
  updateMultipleTrailers,
  updateTrailer,
} from '@store/trailers/trailers.actions';
import {
  initialTrailersState,
  trailerGroupsAdapter,
  trailersAdapter,
  TrailersState,
} from '@store/trailers/trailers.state';
import { AvailableTabsOnPage } from '@store/types/basic-last-page-state';

/**
 * Редьюсер для управления состоянием модуля `trailers`.
 */
export const trailersReducer: ActionReducer<TrailersState> = createReducer(
  initialTrailersState,
  /**
   * Устанавливает флаг загрузки для соответствующей вкладки и сбрасывает ошибку.
   */
  on(loadTrailers, (state) => ({
    ...state,
    lastPageState: {
      ...state.lastPageState,
      [AvailableTabsOnPage.List]: {
        ...state.lastPageState[AvailableTabsOnPage.List],
        loading: true,
      },
    },
    error: undefined,
  })),
  on(loadTrailerGroups, (state) => ({
    ...state,
    lastPageState: {
      ...state.lastPageState,
      [AvailableTabsOnPage.Groups]: {
        ...state.lastPageState[AvailableTabsOnPage.Groups],
        loading: true,
      },
    },
    error: undefined,
  })),

  /**
   * Обрабатывает успешную загрузку списка прицепов.
   */
  on(loadTrailersSuccess, (state, { list }) => ({
    ...state,
    lastPageState: {
      ...state.lastPageState,
      [AvailableTabsOnPage.List]: {
        ...state.lastPageState[AvailableTabsOnPage.List],
        loading: false,
      },
    },
    list: trailersAdapter.setAll(list, state.list),
  })),

  /**
   * Обрабатывает ошибку загрузки списка прицепов.
   */
  on(loadTrailersFailure, (state, { error }) => ({
    ...state,
    lastPageState: {
      ...state.lastPageState,
      [AvailableTabsOnPage.List]: {
        ...state.lastPageState[AvailableTabsOnPage.List],
        loading: false,
      },
    },
    error,
  })),

  /**
   * Обрабатывает успешную загрузку групп прицепов.
   */
  on(loadTrailerGroupsSuccess, (state, { list }) => ({
    ...state,
    lastPageState: {
      ...state.lastPageState,
      [AvailableTabsOnPage.Groups]: {
        ...state.lastPageState[AvailableTabsOnPage.Groups],
        loading: false,
      },
    },
    groupsList: trailerGroupsAdapter.setAll(list, state.groupsList),
  })),

  /**
   * Обрабатывает ошибку загрузки групп прицепов.
   */
  on(loadTrailerGroupsFailure, (state, { error }) => ({
    ...state,
    lastPageState: {
      ...state.lastPageState,
      [AvailableTabsOnPage.Groups]: {
        ...state.lastPageState[AvailableTabsOnPage.Groups],
        loading: false,
      },
    },
    error,
  })),

  /**
   * Добавляет новый прицеп.
   */
  on(addTrailer, (state, { trailer }) => ({
    ...state,
    list: trailersAdapter.addOne(trailer, state.list),
  })),

  /**
   * Обновляет данные прицепа.
   */
  on(updateTrailer, (state, { id, changes }) => ({
    ...state,
    list: trailersAdapter.updateOne({ id: +id, changes }, state.list),
  })),

  /**
   * Обновляет данные нескольких прицепов одновременно.
   */
  on(updateMultipleTrailers, (state, { updates }) => ({
    ...state,
    list: trailersAdapter.updateMany(
      updates.map(({ id, changes }) => ({ id: +id, changes })),
      state.list,
    ),
  })),

  /**
   * Удаляет прицеп.
   */
  on(deleteTrailer, (state, { id }) => ({
    ...state,
    list: trailersAdapter.removeOne(+id, state.list),
  })),

  /**
   * Добавляет новую группу прицепов.
   */
  on(addTrailerGroup, (state, { trailerGroup }) => ({
    ...state,
    groupsList: trailerGroupsAdapter.addOne(trailerGroup, state.groupsList),
  })),

  /**
   * Обновляет данные группы прицепов.
   */
  on(updateTrailerGroup, (state, { id, changes }) => ({
    ...state,
    groupsList: trailerGroupsAdapter.updateOne({ id: +id, changes }, state.groupsList),
  })),

  /**
   * Удаляет группу прицепов.
   */
  on(deleteTrailerGroup, (state, { id }) => ({
    ...state,
    groupsList: trailerGroupsAdapter.removeOne(+id, state.groupsList),
  })),

  /**
   * Обновляет состояние последней страницы.
   */
  on(setLastPageStateTrailers, (state, { newPageState }) => ({
    ...state,
    lastPageState: {
      ...state.lastPageState,
      ...newPageState,
    },
  })),
);
